import { filter, map, kebabCase } from 'lodash';
import PlanDetails from '../common/PlanDetails';
import PlanType from '../common/PlanType';
import Configuration from '../common/Configuration.json';

const hasPlanData = plans => plans.fetched;

const isBetterPlan = (bestPlan, plan) =>
  plan.price > bestPlan.price;

const getBestPlan = (plans) => {
  let bestPlan = { name: '', price: 0 };
  if (hasPlanData(plans)) {
    plans.contractPlans.filter((p) => {
      if (isBetterPlan(bestPlan, p)) {
        bestPlan = p;
        return true;
      }
      return false;
    });
  }
  return bestPlan;
};

/**
 * Gets the credit amount, if there is one, otherwise returns null.
 * @param isContract true if a contract plan is currently selected.
 * @returns the credit amount, if there is one, otherwise null.
 */
const getCredit = (isContract) => {
  if (Configuration.hasAccountCredit && !isContract) {
    return Configuration.creditAmount;
  }
  return null;
};

const getOtherPlans = (availablePlans, currentPlanLabel) =>
  availablePlans.filter(plan => plan.label !== currentPlanLabel);

/**
 * This is an attempt to encapsulate the business rules needed to calculate
 * the amount a Skinny customer will need to pay on sign-up taking into account
 * any discount/s and whether they are joining a 12 month or open term contract.
 * @param isContract true if and only if the customer is signing up to a 12 month
 * contract.
 *
 * @param connectionFee The broadband connection fee (in dollars).
 * @param shippingCost The modem shipping price (in dollars).
 * @param modemPrice The modem price (in dollars).
 * @param discount Sign-up discount amount (in dollars) currently subtracted from modemPrice if present.
 * @returns {
 * {modemCost: number,
 * modemCostLessDiscount: number,
 * connectionCost: number,
 * shippingCost: number,
 * discountApplied: number,
 * total: number}
 * }
 */
const getChargeSummary = (isContract, connectionFee, shippingCost, modemPrice, discount, isByoModemSelected) => {
  let modemCost = isByoModemSelected ? 0 : modemPrice;
  let modemCostLessDiscount = isByoModemSelected ? 0 : modemPrice;
  let connectionCost = connectionFee;
  let discountApplied = 0;
  if (isContract) {
    modemCostLessDiscount = 0;
    modemCost = 0;
    connectionCost = 0;
  } else if (discount && discount.enabled) {
    discountApplied = discount.value;
    modemCostLessDiscount = isByoModemSelected ? 0 : (modemPrice - discount.value);
  }
  const total = (isByoModemSelected ? 0 : shippingCost) + modemCostLessDiscount + connectionCost;
  return {
    modemCost,
    modemCostLessDiscount,
    discountApplied,
    connectionCost,
    shippingCost,
    total,
    isByoModemSelected
  };
};

const filterPlanArray = (plans, label) => plans.filter(plan => plan.label === label)[0];

const getPlanDetails = (plans, label, isContract) => {
  let plan = {};
  if (hasPlanData(plans)) {
    if (isContract) {
      plan = filterPlanArray(plans.contractPlans, label);
    } else {
      plan = filterPlanArray(plans.nonContractPlans, label);
    }
  }
  return plan === undefined ? {} : plan;
};

const canUpgrade = (customerInfo, plans) => {
  if (!customerInfo) {
    return false;
  }
  if (!(customerInfo.connected)) {
    return false;
  }
  if (customerInfo.ufbUpgradeAvailable) {
    return true;
  }
  const bestPlan = getBestPlan(plans);
  return (bestPlan.label && bestPlan.label !== customerInfo.currentPlan);
};

const getDownloadSpeeds = (label) => {
  const matches = filter(PlanDetails.plans, { label });
  return matches[0].download;
};

const getUploadSpeeds = (label) => {
  const matches = filter(PlanDetails.plans, { label });
  return matches[0].upload;
};

const getDescription = (label) => {
  const matches = filter(PlanDetails.plans, { label });
  return matches[0].description;
};

const getPlanData = (label, isContract) => {
  const matches = filter(PlanDetails.plans, { label });
  const plan = matches[0];
  let planId = kebabCase(`skinny-${plan.label}`);
  if (isContract) {
    planId = kebabCase(`skinny-contract-${plan.label}`);
  }
  plan.id = planId;
  return plan;
};

/**
 * A Fibre customer (Change your speed)
 * @returns {boolean}
 */
const hasFibre = (customerInfo) => {
  if (!customerInfo) {
    return false;
  }
  if (!customerInfo.connected) {
    return false;
  }
  const currentPlan = getPlanData(customerInfo.currentPlan, customerInfo.isContract);
  return currentPlan.type === PlanType.UFB;
};

/**
 * Copper customers who are eligible for fibre (Upgrade to fibre)
 * @returns {boolean}
 */
const canUpgradeToFibre = (customerInfo, plans) => {
  if (!customerInfo) {
    return false;
  }
  if (!customerInfo.connected) {
    return false;
  }
  const bestPlan = getBestPlan(plans);
  return bestPlan.label && bestPlan.label !== customerInfo.currentPlan;
};

/**
 * Copper-only customers (Greyed out - Check again later -
 * Direct customer to LFC address search  - Nice to have)
 * @returns {boolean}
 */
const hasDslOnly = (plans) => {
  const bestPlan = getBestPlan(plans);
  return bestPlan.type === PlanType.WVS || bestPlan.type === PlanType.EUBA;
};

/**
 * If the change from oldPlanLabel to newPlanLabel is a move from a non-fibre plan to a fibre plan
 * then return true, otherwise false.
 * @param oldPlanLabel
 * @param newPlanLabel
 * @returns {boolean}
 */
const isFibreUpgrade = (oldPlanLabel, newPlanLabel) => {
  const oldPlan = getPlanData(oldPlanLabel, true);
  const newPlan = getPlanData(newPlanLabel, true);
  return oldPlan.type !== PlanType.UFB && newPlan.type === PlanType.UFB;
};

/**
 * @param oldPlanLabel
 * @param newPlanLabel
 * @returns {boolean} true if both old and new plans are DLS (ASDL or VDSL)
 * otherwise returns false.
 */
const isDSLChange = (oldPlanLabel, newPlanLabel) => {
  const oldPlan = getPlanData(oldPlanLabel, true);
  const newPlan = getPlanData(newPlanLabel, true);
  return oldPlan.type !== PlanType.UFB && newPlan.type !== PlanType.UFB;
};

const getPriceText = price => price ? `$${price}` : null;

const mapPlansToRadios = (plans, currentPlan, isCurrentDisabled) => map(filter(plans, plan => !plan.stopSell),
  plan => ({
    id: plan.planId,
    label: plan.label,
    value: plan.planId,
    previousPrice: getPriceText(plan.oldPrice),
    price: getPriceText(plan.price),
    additionalText2: 'per month',
    description: plan.description,
    planType: plan.type,
    download: plan.download,
    upload: plan.upload,
    isSpecial: (plan.oldPrice && plan.price && plan.price < plan.oldPrice),
    planStateText: plan.label === currentPlan ? 'Current Speed' : null,
    disabled: plan.label === currentPlan && isCurrentDisabled,
    planDealInfo: 'Less any existing credits',
  }));
// Note above ^^^^ if the plan has an old price and it is less then the
// current price we consider the plan to be on special.

const getSalesforceTerm = isContract => isContract ? '12 months' : 'No contract';

export default {
  getBestPlan,
  hasPlanData,
  canUpgrade,
  getPlanDetails,
  getCredit,
  getDownloadSpeeds,
  getUploadSpeeds,
  getChargeSummary,
  mapPlansToRadios,
  getDescription,
  getPlanData,
  hasFibre,
  isFibreUpgrade,
  isDSLChange,
  canUpgradeToFibre,
  hasDslOnly,
  getOtherPlans,
  getSalesforceTerm
};



/** WEBPACK FOOTER **
 ** ./src/services/PlanService.js
 **/